import { supabase } from '../lib/supabase'
import { TemporaryAircraftState, ContainerType } from '../types/database'

/**
 * Service for managing temporary aircraft states during movements
 */
export class TemporaryAircraftStateService {
  private static readonly TABLE_NAME = 'temporary_aircraft_states'

  /**
   * Get active temporary states for a model
   */
  static async getActiveStatesForModel(modelId: number): Promise<TemporaryAircraftState[]> {
    try {
      const { data, error } = await supabase
        .from(this.TABLE_NAME)
        .select('*')
        .eq('model_id', modelId)
        .eq('is_active', true)
        .order('created_at', { ascending: false })

      if (error) {
        throw new Error(`Failed to fetch temporary states: ${error.message}`)
      }

      return data || []
    } catch (error) {
      throw error
    }
  }

  /**
   * Get active temporary state for a specific aircraft
   */
  static async getActiveStateForAircraft(aircraftId: number): Promise<TemporaryAircraftState | null> {
    try {
      const { data, error } = await supabase
        .from(this.TABLE_NAME)
        .select('*')
        .eq('aircraft_id', aircraftId)
        .eq('is_active', true)
        .order('created_at', { ascending: false })
        .limit(1)
        .single()

      if (error) {
        if (error.code === 'PGRST116') {
          // No rows returned
          return null
        }
        throw new Error(`Failed to fetch temporary state: ${error.message}`)
      }

      return data
    } catch (error) {
      return null // Return null instead of throwing for this method
    }
  }

  /**
   * Get aircraft IDs that are currently in temporary states for a specific container
   */
  static async getAircraftIdsInContainer(modelId: number, container: ContainerType): Promise<number[]> {
    try {
      const { data, error } = await supabase
        .from(this.TABLE_NAME)
        .select('aircraft_id')
        .eq('model_id', modelId)
        .eq('container', container)
        .eq('is_active', true)

      if (error) {
        throw new Error(`Failed to fetch aircraft IDs: ${error.message}`)
      }

      return data?.map(item => item.aircraft_id) || []
    } catch (error) {
      return []
    }
  }

  /**
   * Get aircraft IDs that should be excluded from their original container
   */
  static async getAircraftIdsToExclude(modelId: number, originalContainer: ContainerType): Promise<number[]> {
    try {
      const { data, error } = await supabase
        .from(this.TABLE_NAME)
        .select('aircraft_id, container')
        .eq('model_id', modelId)
        .eq('is_active', true)

      if (error) {
        throw new Error(`Failed to fetch aircraft to exclude: ${error.message}`)
      }

      // Return aircraft IDs that are in temporary states for containers OTHER than the original
      return data
        ?.filter(item => item.container !== originalContainer)
        ?.map(item => item.aircraft_id) || []
    } catch (error) {
      return []
    }
  }

  /**
   * Get temporary aircraft data for a specific container
   */
  static async getTemporaryAircraftForContainer(modelId: number, container: ContainerType): Promise<{
    aircraft_id: number
    aircraft_data: any
    override_data?: any
    display_order: number
  }[]> {
    try {
      const { data, error } = await supabase
        .from(this.TABLE_NAME)
        .select('aircraft_id, aircraft_data, override_data, display_order')
        .eq('model_id', modelId)
        .eq('container', container)
        .eq('is_active', true)
        .order('display_order', { ascending: true })

      if (error) {
        throw new Error(`Failed to fetch temporary aircraft: ${error.message}`)
      }

      return data || []
    } catch (error) {
      return []
    }
  }

  /**
   * Check if an aircraft is currently in a temporary state
   */
  static async isAircraftInTemporaryState(aircraftId: number): Promise<boolean> {
    try {
      const { count, error } = await supabase
        .from(this.TABLE_NAME)
        .select('*', { count: 'exact', head: true })
        .eq('aircraft_id', aircraftId)
        .eq('is_active', true)

      if (error) {
        return false
      }

      return (count || 0) > 0
    } catch (error) {
      return false
    }
  }

  /**
   * Get summary of temporary states for a model
   */
  static async getTemporaryStateSummary(modelId: number): Promise<{
    totalActive: number
    byContainer: Record<ContainerType, number>
    aircraftIds: number[]
  }> {
    try {
      const { data, error } = await supabase
        .from(this.TABLE_NAME)
        .select('aircraft_id, container')
        .eq('model_id', modelId)
        .eq('is_active', true)

      if (error) {
        throw new Error(`Failed to fetch summary: ${error.message}`)
      }

      const summary = {
        totalActive: data?.length || 0,
        byContainer: {
          for_sale: 0,
          sold: 0,
          removed_from_market: 0
        } as Record<ContainerType, number>,
        aircraftIds: data?.map(item => item.aircraft_id) || []
      }

      data?.forEach(item => {
        summary.byContainer[item.container as ContainerType]++
      })

      return summary
    } catch (error) {
      return {
        totalActive: 0,
        byContainer: { for_sale: 0, sold: 0, removed_from_market: 0 },
        aircraftIds: []
      }
    }
  }

  /**
   * Deactivate temporary state for an aircraft
   */
  static async deactivateStateForAircraft(aircraftId: number): Promise<void> {
    try {
      const { error } = await supabase
        .from(this.TABLE_NAME)
        .update({ is_active: false })
        .eq('aircraft_id', aircraftId)

      if (error) {
        throw new Error(`Failed to deactivate state: ${error.message}`)
      }
    } catch (error) {
      throw error
    }
  }

  /**
   * Clean up expired temporary states (older than 14 days)
   */
  static async cleanupExpiredStates(): Promise<number> {
    try {
      const fourteenDaysAgo = new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString()

      const { data, error } = await supabase
        .from(this.TABLE_NAME)
        .update({ is_active: false })
        .eq('is_active', true)
        .lt('created_at', fourteenDaysAgo)
        .select('aircraft_id')

      if (error) {
        throw new Error(`Failed to cleanup expired states: ${error.message}`)
      }

      return data?.length || 0
    } catch (error) {
      return 0
    }
  }
}
